import React, { useState } from 'react';
import { Mail, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface NewsletterProps {
  title?: string;
  subtitle?: string;
  className?: string;
}

export const Newsletter: React.FC<NewsletterProps> = ({
  title = "Subscribe to our newsletter",
  subtitle = "Get the latest financial tips and insights delivered straight to your inbox.",
  className = ""
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setStatus('success');
    setIsLoading(false);
    setEmail('');

    // Reset success message after 3 seconds
    setTimeout(() => {
      setStatus('idle');
    }, 3000);
  };

  return (
    <div className={`glass-card p-8 ${className}`}>
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-xl bg-emerald-500/10">
          <Mail className="w-6 h-6 text-emerald-500" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold mb-2">{title}</h3>
          <p className="text-slate-400 mb-6">{subtitle}</p>
          
          <form onSubmit={handleSubmit} className="relative">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setStatus('idle') || setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 bg-slate-800/50 rounded-lg border border-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-colors"
                  disabled={isLoading || status === 'success'}
                />
              </div>
              <button
                type="submit"
                disabled={!email || isLoading || status === 'success'}
                className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center gap-2 transition-all"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white" />
                ) : (
                  <>
                    Subscribe
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>

            <AnimatePresence mode="wait">
              {status === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute left-0 -bottom-8 flex items-center gap-1.5 text-emerald-500"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm">Thanks for subscribing!</span>
                </motion.div>
              )}
              {status === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute left-0 -bottom-8 flex items-center gap-1.5 text-red-500"
                >
                  <XCircle className="w-4 h-4" />
                  <span className="text-sm">Something went wrong. Please try again.</span>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>
      </div>
    </div>
  );
};